import { inject, Injectable } from '@angular/core';
import { ConceptStatus, KnowledgeUserState } from '@dataclouder/ngx-knowledge';

import { KnowledgeWordCoreService } from './knowledge-words-core.service';
import { KnowledgeWordStateService } from './knowledge-words-state.service';
import { LangWord } from './lexicore.classes';
import { UserService } from '../data-services/user.service';

// Basically this service is used to cross core and state so i avoid circular dependency.
// Also operations that requieres both services, core and state, like clean memory and db.
@Injectable({
  providedIn: 'root',
})
export class WordsCoreStateManagerService {
  private knowledgeWordStateService = inject(KnowledgeWordStateService);
  private lexicoreKnowledgeService = inject(KnowledgeWordCoreService);
  private userService = inject(UserService);

  public async getLearningWords(targetLang: string = null) {
    const base = this.userService.getBaseLanguage();
    targetLang = targetLang || this.userService.getTargetLanguage();

    const learningWords = await this.knowledgeWordStateService.getKnowledgeWordStateArray(targetLang);
    const words = learningWords.filter((word) => word.status === ConceptStatus.Learning);
    const lexicoreWords = await this.lexicoreKnowledgeService.getLexicoreDict(targetLang);

    // Temporal Fix.
    const result = words.map((word) => ({
      ...lexicoreWords[word.concept],
      translation: lexicoreWords[word.concept]?.[base]?.translation,
      otherTranslations: lexicoreWords[word.concept]?.[base]?.otherTranslations,
    }));

    return result;
  }

  public async getStatusWords(): Promise<Record<string, KnowledgeUserState>> {
    const targetLang = this.userService.getTargetLanguage();
    const fullState = await this.knowledgeWordStateService.getKnowledgeWordStateDict(targetLang);
    return fullState;
  }

  public async updateCoreState(word: LangWord, knowledgeUserState: KnowledgeUserState): Promise<void> {
    // Uses this when is a new word.
    const targetLang = this.userService.getTargetLanguage();
    const r1 = await this.lexicoreKnowledgeService.updateMemoryAndDB(targetLang, [word]);
    const r2 = await this.knowledgeWordStateService.updateMemoryAndDB(targetLang, [knowledgeUserState]);
    console.log(r1, r2);
  }

  public clearCoreStateMemoryAndDB(): void {
    this.lexicoreKnowledgeService.clearMemoryAndDB();
    this.knowledgeWordStateService.clearMemoryAndDB();
  }

  public async refreshCoreState(): Promise<void> {
    this.clearCoreStateMemoryAndDB();
    const targetLang = this.userService.getTargetLanguage();
    const lastestWordsCore = await this.lexicoreKnowledgeService.getLexicoreDict(targetLang);
    console.log(lastestWordsCore);
  }

  public async getStats(): Promise<WordsStats> {
    const targetLang = this.userService.getTargetLanguage();
    const knowledgeWordStateArray = await this.knowledgeWordStateService.getKnowledgeWordStateArray(targetLang);

    const wordsLearning = knowledgeWordStateArray.filter((word) => word.status === ConceptStatus.Learning);
    const wordsAlreadyKnown = knowledgeWordStateArray.filter((word) => word.status === ConceptStatus.AlreadyKnown);
    const wordsLearned = knowledgeWordStateArray.filter((word) => word.status === ConceptStatus.Learned);

    return {
      learningWords: wordsLearning.length,
      alreadyKnownWords: wordsAlreadyKnown.length,
      learnedWords: wordsLearned.length,
      totalWords: knowledgeWordStateArray.length,
      currentLevelWords: wordsLearning.length + wordsAlreadyKnown.length + wordsLearned.length,
    };
  }

  public async isWordInState(word: string): Promise<KnowledgeUserState | null> {
    const targetLang = this.userService.getTargetLanguage();
    const knowledgeWordStateArray = await this.knowledgeWordStateService.getKnowledgeWordStateDict(targetLang);
    return knowledgeWordStateArray[word] || null;
  }
}

export interface WordsStats {
  learningWords: number;
  alreadyKnownWords: number;
  learnedWords: number;
  totalWords: number;
  currentLevelWords: number;
}
